import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PackageOpen, PlusCircle } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import type { CustomerOpenOrder } from '../../types/nursery';

export interface CustomerOpenOrdersProps {
  customerId: number;
  /** Carry on entering this order on the sheet instead of starting a new one. */
  onAddTo: (orderId: number) => void;
}

/**
 * On a new order, once the customer is chosen: the orders they already have
 * that are not being picked yet (Rob, 2026-10-19). Each can be added to on
 * the sheet, or, once confirmed, drawn down: some of it taken now onto an
 * order of its own (/nursery/orders/:id/draw-down). Typing a quantity starts
 * a new order as before, and the panel goes.
 *
 * Shows nothing while loading, when there are none, or if it cannot load:
 * entering an order never waits on it.
 */
export function CustomerOpenOrders({ customerId, onAddTo }: CustomerOpenOrdersProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'CustomerOpenOrders' });
  const tz = useOrgTimezone();
  const [orders, setOrders] = useState<CustomerOpenOrder[]>([]);

  useEffect(() => {
    let cancelled = false;
    setOrders([]);
    nurseryService.getCustomerOpenOrders(customerId).then((res) => {
      if (!cancelled && res.success) setOrders(res.data || []);
    }).catch(() => { /* the panel is an offer; without it a new order is entered as before */ });
    return () => { cancelled = true; };
  }, [customerId]);

  if (!orders.length) return null;

  return (
    <section
      aria-labelledby="customer-open-orders"
      className="bg-sky-50 border border-sky-200 rounded-lg p-3 space-y-3"
    >
      <div>
        <h4 id="customer-open-orders" className="text-sm font-semibold text-sky-900">
          {t('heading', { count: orders.length })}
        </h4>
        <p className="text-xs text-sky-900/80 mt-0.5 break-words">{t('intro')}</p>
      </div>
      <ul className="space-y-2">
        {orders.map((o) => (
          <li key={o.id} className="bg-white border border-sky-100 rounded-lg px-3 py-2 space-y-2 min-w-0">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Link to={`/nursery/orders/${o.id}`} className="text-sm font-medium text-primary-700 hover:underline break-all">
                  {o.order_number}
                </Link>
                <span className="text-xs text-neutral-600">{t(`status.${o.status}`, { defaultValue: o.status })}</span>
                {o.held_for_drawdown && (
                  <span className="text-xs font-medium bg-violet-100 text-violet-800 rounded px-1.5 py-0.5">{t('held')}</span>
                )}
              </div>
              <p className="text-xs text-neutral-500 break-words">
                {t('details', { date: formatLocalDateInTz(o.order_date, tz), count: o.total_trees })}
                {o.customer_po && <span> · {t('po', { po: o.customer_po })}</span>}
                {o.drawn_trees > 0 && <span> · {t('drawn', { count: o.drawn_trees })}</span>}
                {o.load_number && <span> · {t('onLoad', { load: o.load_number })}</span>}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => onAddTo(o.id)}
                className="inline-flex items-center gap-1 rounded-md border border-neutral-300 bg-white px-2.5 py-1 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
              >
                <PlusCircle className="h-4 w-4" /> {t('addTo')}
              </button>
              {o.can_draw && (
                <Link
                  to={`/nursery/orders/${o.id}/draw-down`}
                  className="inline-flex items-center gap-1 rounded-md bg-primary-600 px-2.5 py-1 text-sm font-medium text-white hover:bg-primary-700"
                >
                  <PackageOpen className="h-4 w-4" /> {t('drawDown')}
                </Link>
              )}
            </div>
          </li>
        ))}
      </ul>
      <p className="text-xs text-sky-900/80 break-words">{t('orNew')}</p>
    </section>
  );
}

export default CustomerOpenOrders;
